import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import { Player } from '@lottiefiles/react-lottie-player';
import InviteTypeWriter from './InviteTypeWriter';
import envelopeAnimation from '../../assets/misc/envelope-open.json';
import whiteLogo from '../../assets/images/white_logo.png';
import miniLogo from '../../assets/icons/minilogo.svg';

const backgroundGradient = {
  minHeight: '100vh',
  background: 'linear-gradient(-45deg,rgb(2, 4, 9),rgb(3, 19, 61),rgb(3, 9, 27),rgb(0, 8, 31))',
  backgroundSize: '400% 400%',
  animation: 'gradientAnimation 15s ease infinite',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  overflow: 'hidden',
  position: 'relative',
};

const InviteEnvelopeWrapper = () => {
  const { nameParam } = useParams();

  // Envelope animation states
  const [opened, setOpened] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const playerRef = useRef(null);
  const [showLogo, setShowLogo] = useState(false);

  // Backend-driven expiry states
  const [inviteExpiry, setInviteExpiry] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isExpired, setIsExpired] = useState(false);

  // Validate name
  const isValidName =
    nameParam && nameParam.split('-').every((part) => /^[a-z]+$/i.test(part));

  // Capitalize each word if valid
  let displayName = '';
  if (isValidName) {
    displayName = nameParam
      .split('-')
      .map(
        (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join(' ');
  }

  useEffect(() => {
    // Set background color for the body when the SurveyPage is rendered
    document.body.style.backgroundColor = "rgb(3, 19, 61)"; // Your desired background color

    // Cleanup function to reset the background color
    return () => {
        document.body.style.backgroundColor = ""; // Reset to default
    };
}, []);


  // Tracking function
  const sendTrackingData = (status) => {
    const data = new FormData();
    data.append('unique_id', nameParam);
    const now = new Date();
    const screenMap = {
      landed: 'screen1',
      'envelope open': 'screen2',
      'interview button': 'screen3',
      'expired envelope wrapper': 'screen4',
      'expired invitation': 'screen5',
      'something went wrong': 'screen6',
    };

    if (screenMap[status]) {
      const formattedDate = now.toLocaleString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: true,
      });
      data.append(screenMap[status], formattedDate);
      const BASE_URL = process.env.REACT_APP_API_URL;
      navigator.sendBeacon(`${BASE_URL}/api/track-application/`, data);
    } else {
      console.warn(`Invalid tracking status: ${status}`);
    }
  };

  // Track "landed" once
  useEffect(() => {
    sendTrackingData('landed');
    // eslint-disable-next-line
  }, []);

  // If name is invalid, track "something went wrong"
  useEffect(() => {
    if (!isValidName) {
      sendTrackingData('something went wrong');
    }
    // eslint-disable-next-line
  }, [isValidName]);

  // Fetch invite data from the backend
  useEffect(() => {
    const fetchInvite = async () => {
      if (!isValidName) return; // no point fetching if name is invalid

      try {
        const BASE_URL = process.env.REACT_APP_API_URL;
        const response = await fetch(
          `${BASE_URL}/api/fancy-custom-invite/${nameParam}/`
        );
        if (!response.ok) {
          console.error('Failed to fetch invite data');
          sendTrackingData('something went wrong');
          return;
        }
        const data = await response.json();
        setInviteExpiry(data.expiry_time);
      } catch (error) {
        console.error('Error fetching invite:', error);
        sendTrackingData('something went wrong');
      }
    };

    fetchInvite();
    // eslint-disable-next-line
  }, [nameParam, isValidName]);

  // Calculate time left in seconds
  const calculateTimeLeft = () => {
    if (!inviteExpiry) return 0;
    const now = new Date();
    const expireDate = new Date(inviteExpiry);
    const diff = expireDate - now;
    return Math.max(Math.floor(diff / 1000), 0);
  };

  // Initialize timeLeft
  useEffect(() => {
    if (inviteExpiry) {
      const initialTimeLeft = calculateTimeLeft();
      setTimeLeft(initialTimeLeft);
      if (initialTimeLeft <= 0) {
        setIsExpired(true);
        sendTrackingData('expired envelope wrapper');
      }
    }
    // eslint-disable-next-line
  }, [inviteExpiry]);

  // Update countdown every second
  useEffect(() => {
    if (!inviteExpiry) return;

    const intervalId = setInterval(() => {
      const newTimeLeft = calculateTimeLeft();
      setTimeLeft(newTimeLeft);
      if (newTimeLeft <= 0) {
        setIsExpired(true);
        sendTrackingData('expired envelope wrapper');
        clearInterval(intervalId);
      }
    }, 1000);

    return () => clearInterval(intervalId);
    // eslint-disable-next-line
  }, [inviteExpiry]);

  // Auto-open envelope animation
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsPlaying(true);
      if (playerRef.current) {
        playerRef.current.play();
        setTimeout(() => {
          setShowLogo(true);
        }, 1000);
        setTimeout(() => {
          playerRef.current.pause();
          setOpened(true);
          sendTrackingData('envelope open');
        }, 2000);
      }
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, []);

  // If invalid name, show error screen
  if (!isValidName) {
    return (
      <div
        style={{
          ...backgroundGradient,
          flexDirection: 'column',
          padding: '20px',
          textAlign: 'center',
        }}
      >
        <motion.img
          src={whiteLogo}
          alt="Logo"
          style={{ width: '200px', marginBottom: '30px', zIndex: 2 }}
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        />
        <motion.h1
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
          style={{
            color: 'white',
            marginBottom: '20px',
            fontFamily: 'Space Grotesk, sans-serif',
            fontWeight: 600,
            zIndex: 2,
          }}
        >
          Something went wrong
        </motion.h1>
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.4 }}
          style={{
            color: '#eee',
            maxWidth: '400px',
            lineHeight: '1.6',
            fontFamily: 'Space Grotesk, sans-serif',
            zIndex: 2,
          }}
        >
          This invite link appears to be invalid. Please make sure you're using
          the exact link that was provided.
        </motion.p>
      </div>
    );
  }

  // If expired, show expired screen
  if (isExpired) {
    return (
      <div style={backgroundGradient}>
        <div style={{ color: '#fff', textAlign: 'center', zIndex: 2 }}>
          <motion.h2
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
            style={{
              fontFamily: 'Space Grotesk, sans-serif',
              marginBottom: '15px',
            }}
          >
            Invitation Expired
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1.2 }}
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
          >
            If you believe this is an error, email{' '}
            <a
              href="mailto:applications@prospinity.com"
              style={{ color: '#4ea6ff' }}
            >
              applications@prospinity.com
            </a>.
          </motion.p>
        </div>
      </div>
    );
  }

  // Envelope container and text shimmer variants
  const envelopeVariants = {
    initial: { opacity: 0, scale: 0.9 },
    animate: { opacity: 1, scale: 1 },
    exit: { opacity: 0, scale: 1.2 },
  };

  const shimmerVariants = {
    initial: { opacity: 0 },
    animate: {
      opacity: 1,
      transition: {
        duration: 0.8,
        yoyo: Infinity,
      },
    },
  };

  return (
    <div style={backgroundGradient}>
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backdropFilter: 'blur(6px)',
        }}
      />
      <AnimatePresence mode="wait">
        {!opened && (
          <motion.div
            key="envelope"
            variants={envelopeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.8 }}
            style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              width: '100%',
              maxWidth: '80vw',
              margin: '0 auto',
              padding: '40px 20px',
              zIndex: 2,
            }}
          >
            <motion.img
              src={whiteLogo}
              alt="Logo"
              style={{ width: '40%', maxWidth: '280px', marginBottom: '15px' }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            />
            <motion.p
              style={{
                color: '#fff',
                fontSize: '24px',
                fontWeight: '500',
                marginBottom: '20px',
                letterSpacing: '0.5px',
                fontFamily: 'Space Grotesk, sans-serif',
              }}
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              {displayName}
            </motion.p>
            <div
              style={{
                position: 'relative',
                width: '80%',
                maxWidth: '400px',
                perspective: '1000px',
              }}
            >
              <motion.div
                whileHover={{ rotateY: 5 }}
                style={{
                  transformStyle: 'preserve-3d',
                }}
              >
                <Player
                  ref={playerRef}
                  autoplay={false}
                  loop={false}
                  src={envelopeAnimation}
                  style={{ width: '100%', height: 'auto' }}
                />
                {showLogo && (
                  <motion.img
                    src={miniLogo}
                    alt="Prospinity Logo"
                    initial={{ opacity: 0, scale: 0.5 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{ duration: 0.8 }}
                    style={{
                      position: 'absolute',
                      width: '14%',
                      top: '17%',
                      left: '45%',
                      transform: 'translate(-50%, -50%)',
                      zIndex: 2,
                    }}
                  />
                )}
              </motion.div>
            </div>
            <motion.div
              variants={shimmerVariants}
              initial="initial"
              animate="animate"
              style={{
                marginTop: '20px',
                padding: '12px 24px',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '10px',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                backdropFilter: 'blur(10px)',
                boxShadow: '0 0 20px rgba(255, 255, 255, 0.1)',
              }}
            >
              <p
                style={{
                  color: '#fff',
                  fontSize: '16px',
                  fontWeight: '500',
                  letterSpacing: '0.5px',
                  textTransform: 'uppercase',
                  fontFamily: 'Space Grotesk, sans-serif',
                  margin: 0,
                }}
              >
                Opening invite...
              </p>
            </motion.div>
          </motion.div>
        )}

        {opened && (
          <motion.div
            key="invite"
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.8 }}
            style={{
              width: '100%',
              maxWidth: '100%',
              position: 'relative',
              zIndex: 2,
            }}
          >
            <div style={{ color: '#fff', textAlign: 'center' }}>
              {/* Removed the big "Expires in" banner here */}
              <InviteTypeWriter
                name={displayName}
                timeLeft={timeLeft}
                sendTrackingData={sendTrackingData}
              />
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <style>
        {`
          @keyframes gradientAnimation {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
        `}
      </style>
    </div>
  );
};

export default InviteEnvelopeWrapper;